import * as React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import WorkIcon from "@mui/icons-material/Work";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SchoolIcon from "@mui/icons-material/School";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router";

function JobForm({
  formState,
  onFieldChange,
  onSubmit,
  onReset,
  submitButtonLabel,
  backButtonPath,
}) {
  const { values: formValues = {}, errors: formErrors = {} } = formState || {};
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // ref for jobSummary textarea
  const summaryRef = React.useRef(null);

  const adjustSummaryHeight = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
    el.style.overflow = "hidden";
  };

  React.useEffect(() => {
    adjustSummaryHeight(summaryRef.current);
  }, [formValues.jobSummary]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit(formValues);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(backButtonPath ?? "/app/jobs");
  };

  const handleTextChange = (e) => {
    onFieldChange(e.target.name, e.target.value);
    if (e.target.name === "jobSummary") {
      adjustSummaryHeight(e.target);
    }
  };

  const handleNumberChange = (e) => {
    onFieldChange(e.target.name, Number(e.target.value));
  };

  const handleSelectChange = (e) => {
    onFieldChange(e.target.name, e.target.value);
  };

  const handleChipAdd = (field, value) => {
    if (!value || !value.trim()) return;
    const updated = [...(formValues[field] || []), value.trim()];
    onFieldChange(field, updated);
  };

  const handleChipDelete = (field, chipToDelete) => {
    const updated = (formValues[field] || []).filter((chip) => chip !== chipToDelete);
    onFieldChange(field, updated);
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      autoComplete="off"
      sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 3 }}
    >
      {/* Job Information */}
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <WorkIcon color="primary" />
            <Typography variant="h6">Job Information</Typography>
          </Stack>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="jobTitle"
                label="Job Title"
                value={formValues.jobTitle ?? ""}
                onChange={handleTextChange}
                error={!!formErrors.jobTitle}
                helperText={formErrors.jobTitle ?? " "}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="department"
                label="Department"
                value={formValues.department ?? ""}
                onChange={handleTextChange}
                error={!!formErrors.department}
                helperText={formErrors.department ?? " "}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="location"
                label="Location"
                value={formValues.location ?? ""}
                onChange={handleTextChange}
                error={!!formErrors.location}
                helperText={formErrors.location ?? " "}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="employment-type-label">Employment Type</InputLabel>
                <Select
                  name="employmentType"
                  value={formValues.employmentType ?? ""}
                  onChange={handleSelectChange}
                  labelId="employment-type-label"
                >
                  <MenuItem value="Full-time">Full-time</MenuItem>
                  <MenuItem value="Part-time">Part-time</MenuItem>
                  <MenuItem value="Contract">Contract</MenuItem>
                  <MenuItem value="Internship">Internship</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
        <CardContent>
          <Grid item xs={12} sm={6}>
              <TextField
                name="jobSummary"
                label="Job Summary"
                value={formValues.jobSummary ?? ""}
                onChange={handleTextChange}
                fullWidth
                inputRef={summaryRef}
              ></TextField>
            </Grid>
        </CardContent>
      </Card>

      {/* Responsibilities & Skills */}
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <AssignmentIcon color="primary" />
            <Typography variant="h6">Responsibilities & Skills</Typography>
          </Stack>
          <Divider sx={{ mb: 2 }} />

          {/* Responsibilities */}
          <Typography variant="subtitle2">Responsibilities</Typography>
          <Stack direction="row" flexWrap="wrap" my={2}>
            {(formValues.responsibilities || []).map((r, i) => (
              <Chip
                sx={{m:0.5}}
                key={i}
                label={r}
                onDelete={() => handleChipDelete("responsibilities", r)}
                color="primary"
                variant="outlined"
              />
            ))}
          </Stack>
          <TextField
            placeholder="Add responsibility"
            fullWidth
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleChipAdd("responsibilities", e.target.value);
                e.target.value = "";
              }
            }}
          />

          {/* Required Skills */}
          <Typography variant="subtitle2" mt={3}>
            Required Skills
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
            {(formValues.requiredSkills || []).map((s, i) => (
              <Chip
                key={i}
                label={s}
                onDelete={() => handleChipDelete("requiredSkills", s)}
                color="success"
                variant="outlined"
              />
            ))}
          </Stack>
          <TextField
            placeholder="Add required skill"
            fullWidth
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleChipAdd("requiredSkills", e.target.value);
                e.target.value = "";
              }
            }}
          />

          {/* Preferred Skills */}
          <Typography variant="subtitle2" mt={3}>
            Preferred Skills
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
            {(formValues.preferredSkills || []).map((s, i) => (
              <Chip
                key={i}
                label={s}
                onDelete={() => handleChipDelete("preferredSkills", s)}
                color="info"
                variant="outlined"
              />
            ))}
          </Stack>
          <TextField
            placeholder="Add preferred skill"
            fullWidth
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleChipAdd("preferredSkills", e.target.value);
                e.target.value = "";
              }
            }}
          />
        </CardContent>
      </Card>

      {/* Requirements & Compensation */}
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <SchoolIcon color="primary" />
            <Typography variant="h6">Requirements & Compensation</Typography>
          </Stack>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="experienceLevel"
                label="Experience Level"
                value={formValues.experienceLevel ?? ""}
                onChange={handleTextChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="educationRequirements"
                label="Education Requirements"
                value={formValues.educationRequirements ?? ""}
                onChange={handleTextChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                type="number"
                name="minSalary"
                label="Minimum Salary"
                value={formValues.minSalary ?? ""}
                onChange={handleNumberChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                type="number"
                name="maxSalary"
                label="Maximum Salary"
                value={formValues.maxSalary ?? ""}
                onChange={handleNumberChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="currency-label">Currency</InputLabel>
                <Select
                  name="currency"
                  value={formValues.currency ?? "INR"}
                  onChange={handleSelectChange}
                  labelId="currency-label"
                >
                  <MenuItem value="INR">INR</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Actions */}
      <Stack direction="row" spacing={2} justifyContent="space-between">
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back
        </Button>
        <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
          {submitButtonLabel}
        </Button>
      </Stack>
    </Box>
  );
}

JobForm.propTypes = {
  backButtonPath: PropTypes.string,
  formState: PropTypes.object.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onReset: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  submitButtonLabel: PropTypes.string.isRequired,
};

export default JobForm;
